import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Label, ListPeoplePicker, IPersonaProps, IBasePickerSuggestionsProps } from "@fluentui/react";
import { FiHelpCircle } from 'react-icons/fi';

import styles from "./UserPicker.module.css"

import { getToken } from "../../authConfig";
import { handleError } from "../../toaster";
import { PermissionsUser, searchUsers } from "../../api";

interface Props {
  selectedUsers: PermissionsUser[];
  onUsersChange: (updatedUsers: PermissionsUser[]) => void;
  useDarkMode: boolean;
};

export const UserPicker = ({ selectedUsers, onUsersChange, useDarkMode }: Props) => {
  const client = useMsal().instance;
  const [filterText, setFilterText] = useState<string>("");
  const tooltipHtml = "Select users to be given access to the file(s)";
  const suggestionProps: IBasePickerSuggestionsProps = {
    suggestionsHeaderText: 'Suggested Users',
    noResultsFoundText: filterText.length < 3 ? 'Type 3 or more characters' : 'No results found',
    loadingText: 'Loading',
  };

  const onPickerChange = (items?: IPersonaProps[]) => {
    if (items) {
      const newUsers = items
        .map(item => ({
          id: item.key as string,
          displayName: item.text as string,
          userPrincipalName: item.secondaryText as string
        }));
      onUsersChange(newUsers);
    }
  };

  const onRemoveUser = (item: IPersonaProps): void => {
    const updatedUsers = selectedUsers.filter(user => user.id !== item.key);
    onUsersChange(updatedUsers);
  };

  const onFilterChanged = useCallback(async (filterText: string): Promise<IPersonaProps[]> => {
    setFilterText(filterText);
    if (filterText.length >= 3) {
      try {
        const token = client ? await getToken(client) : undefined;
        const result = await searchUsers(token, filterText);
        const userSuggestions: IPersonaProps[] = result
          .filter(user => !selectedUsers.some(selected => selected.id === user.id))
          .map(user => ({
            key: user.id,
            text: user.displayName,
            secondaryText: user.userPrincipalName,
          }));
        return userSuggestions.filter(persona => persona.text?.toLowerCase().includes(filterText.toLowerCase()));
      } catch (error) {
        handleError(error, "An error occurred fetching users");
      }
    }
    return [];
  }, [selectedUsers]);

  return (
    <div className={styles.contentArea}>
      <div className={styles.pickerArea}>
        <Label>Users&nbsp;
          <FiHelpCircle
            data-tooltip-id="UserPicker-tooltip"
            data-tooltip-html={tooltipHtml}>
          </FiHelpCircle>
        </Label>
        <ListPeoplePicker
          onResolveSuggestions={onFilterChanged}
          getTextFromItem={(item: IPersonaProps) => item.text || ''}
          onChange={onPickerChange}
          pickerSuggestionsProps={suggestionProps}
          selectedItems={selectedUsers.map(user => ({
            key: user.id,
            text: user.displayName,
            secondaryText: user.userPrincipalName,
          }))}
          inputProps={{
            placeholder: 'Select users to add',
          }}
          onRemoveSuggestion={onRemoveUser}
          resolveDelay={300}
        />
      </div>
    </div>
  );
};